import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NAV_SECTIONS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'tasks', label: 'My Tasks' },
  { key: 'meetings', label: 'Meetings' },
  { key: 'messages', label: 'Messages' },
  { key: 'announcements', label: 'Announcements' },
  { key: 'profile', label: 'My Profile' },
];

const PRIORITY_STYLES = {
  urgent: 'bg-red-500/15 text-red-300 border-red-500/40',
  high: 'bg-orange-500/15 text-orange-300 border-orange-500/40',
  medium: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/40',
  low: 'bg-gray-500/15 text-gray-300 border-gray-500/40',
};

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
];

const resolveImageUrl = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return `${BACKEND_URL}${url.startsWith('/') ? url : `/${url}`}`;
};

const formatDate = (value) => {
  if (!value) return 'No deadline';
  try {
    return new Date(value).toLocaleDateString();
  } catch (error) {
    return String(value);
  }
};

const formatAttachmentDate = (value) => {
  if (!value) return 'Unknown date';
  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return String(value);
  }
};

const formatAttachmentSize = (bytes) => {
  const value = Number(bytes || 0);
  if (!Number.isFinite(value) || value <= 0) return '0 B';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
};

const getAttachmentHref = (attachment, preview = false) => {
  if (!attachment) return '';
  if (preview && attachment.preview_url) return `${BACKEND_URL}${attachment.preview_url}`;
  if (attachment.download_url) return `${BACKEND_URL}${attachment.download_url}`;
  if (attachment.url) {
    if (/^https?:\/\//i.test(attachment.url)) return attachment.url;
    return `${BACKEND_URL}${attachment.url.startsWith('/') ? attachment.url : `/${attachment.url}`}`;
  }
  return '';
};

const isImageAttachment = (attachment) => String(attachment?.content_type || '').toLowerCase().startsWith('image/');
const isVideoAttachment = (attachment) => String(attachment?.content_type || '').toLowerCase().startsWith('video/');

const formatDateTime = (value) => {
  if (!value) return 'Unknown time';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
};

const formatDateLabel = (value) => {
  if (!value) return 'Unknown date';
  try {
    return new Date(value).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return String(value);
  }
};

const groupByDateLabel = (entries) => {
  const grouped = [];
  let lastLabel = '';
  (entries || []).forEach((entry) => {
    const label = formatDateLabel(entry?.created_at || entry?.updated_at || entry?.uploaded_at);
    if (label !== lastLabel) {
      grouped.push({ type: 'separator', id: `sep-${label}-${grouped.length}`, label });
      lastLabel = label;
    }
    grouped.push({ type: 'item', id: entry?.id || `${grouped.length}`, value: entry });
  });
  return grouped;
};

const sortNotificationsByDateDesc = (notifications) => {
  return [...(notifications || [])].sort((a, b) => new Date(b?.created_at || 0).getTime() - new Date(a?.created_at || 0).getTime());
};

const getAttachmentTypeLabel = (attachment) => {
  const contentType = String(attachment?.content_type || '').toLowerCase();
  if (contentType.startsWith('image/')) return 'Image';
  if (contentType.includes('pdf')) return 'PDF';
  if (contentType.includes('sheet') || contentType.includes('excel') || contentType.includes('csv')) return 'Spreadsheet';
  if (contentType.includes('word') || contentType.includes('text')) return 'Document';
  if (contentType.includes('presentation') || contentType.includes('powerpoint')) return 'Presentation';
  return 'File';
};

const getErrorMessage = (requestError, fallback = 'Something went wrong') => {
  const detail = requestError?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === 'string') return item;
        const field = Array.isArray(item?.loc) ? item.loc[item.loc.length - 1] : null;
        return field ? `${field}: ${item.msg}` : item?.msg;
      })
      .filter(Boolean)
      .join('; ') || fallback;
  }
  if (typeof detail === 'object') return detail.msg || fallback;
  return fallback;
};

const EmployeePortal = ({ user }) => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');

  const [dashboard, setDashboard] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [conversationUsers, setConversationUsers] = useState([]);
  const [conversationSearch, setConversationSearch] = useState('');
  const [conversationDepartmentFilter, setConversationDepartmentFilter] = useState('');
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState('');
  const [activeConversation, setActiveConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [newConversationUserId, setNewConversationUserId] = useState('');
  const [newConversationTitle, setNewConversationTitle] = useState('');
  const [conversationMessageDraft, setConversationMessageDraft] = useState('');
  const [conversationAttachmentDraft, setConversationAttachmentDraft] = useState([]);
  const [conversationAttachmentUploading, setConversationAttachmentUploading] = useState(false);
  const [conversationUploadProgress, setConversationUploadProgress] = useState(0);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState('');
  const [editingMessageText, setEditingMessageText] = useState('');
  const [messageActionError, setMessageActionError] = useState('');
  const [messageActionSuccess, setMessageActionSuccess] = useState('');
  const [profileForm, setProfileForm] = useState({
    phone_number: '',
    address: '',
    city: '',
    province_state: '',
    postal_code: '',
    country: '',
    bio: '',
    emergency_contact_name: '',
    emergency_contact_relationship: '',
    emergency_contact_phone: '',
  });

  const [commentDraftByTask, setCommentDraftByTask] = useState({});
  const [noteDraftByTask, setNoteDraftByTask] = useState({});
  const [uploadingTaskId, setUploadingTaskId] = useState('');

  const profile = dashboard?.profile || user || {};
  const stats = dashboard?.stats || {};

  const hydrateProfileForm = useCallback((source) => {
    setProfileForm({
      phone_number: source?.phone_number || '',
      address: source?.address || '',
      city: source?.city || '',
      province_state: source?.province_state || '',
      postal_code: source?.postal_code || '',
      country: source?.country || '',
      bio: source?.bio || '',
      emergency_contact_name: source?.emergency_contact_name || '',
      emergency_contact_relationship: source?.emergency_contact_relationship || '',
      emergency_contact_phone: source?.emergency_contact_phone || '',
    });
  }, []);

  const loadDashboard = useCallback(async () => {
    const response = await axios.get(`${API}/dashboard/employee`);
    setDashboard(response.data || null);
    hydrateProfileForm(response.data?.profile || user);
  }, [hydrateProfileForm, user]);

  const loadTasks = useCallback(async () => {
    const response = await axios.get(`${API}/employee/tasks`);
    setTasks(response.data?.tasks || []);
  }, []);

  const loadMeetings = useCallback(async () => {
    const response = await axios.get(`${API}/employee/meetings`);
    setMeetings(response.data?.meetings || []);
  }, []);

  const loadConversationUsers = useCallback(async (searchTerm = '', department = '') => {
    const response = await axios.get(`${API}/employee/conversations/users`, {
      params: {
        search: searchTerm || undefined,
        department: department || undefined,
      },
    });
    setConversationUsers(response.data?.users || []);
  }, []);

  const loadConversations = useCallback(async (searchTerm = '') => {
    const response = await axios.get(`${API}/employee/conversations`, {
      params: { search: searchTerm || undefined },
    });
    setConversations(response.data?.conversations || []);
  }, []);

  const loadConversationDetail = useCallback(async (conversationId) => {
    if (!conversationId) {
      setActiveConversation(null);
      setConversationMessages([]);
      return;
    }
    setConversationLoading(true);
    try {
      const response = await axios.get(`${API}/employee/conversations/${conversationId}`);
      setActiveConversation(response.data?.conversation || null);
      setConversationMessages(response.data?.messages || []);
      setActiveConversationId(conversationId);
      await axios.post(`${API}/employee/conversations/${conversationId}/read`);
    } finally {
      setConversationLoading(false);
    }
  }, []);

  const refreshPortal = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await Promise.all([loadDashboard(), loadTasks(), loadMeetings(), loadConversations(), loadConversationUsers()]);
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'Unable to load employee portal'));
    } finally {
      setLoading(false);
    }
  }, [loadDashboard, loadTasks, loadMeetings, loadConversations, loadConversationUsers]);

  useEffect(() => {
    refreshPortal();
  }, [refreshPortal]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedTab = params.get('tab');
    const requestedConversationId = params.get('conversation_id');
    if (requestedTab) {
      setActiveTab(requestedTab);
    }
    if (requestedConversationId) {
      loadConversationDetail(requestedConversationId).catch(() => {
        setMessageActionError('Unable to open the linked conversation');
      });
    }
  }, [location.search, loadConversationDetail]);

  useEffect(() => {
    if (!feedback) return;
    const timer = window.setTimeout(() => setFeedback(''), 3500);
    return () => window.clearTimeout(timer);
  }, [feedback]);

  useEffect(() => {
    if (!messageActionSuccess) return;
    const timer = window.setTimeout(() => setMessageActionSuccess(''), 3500);
    return () => window.clearTimeout(timer);
  }, [messageActionSuccess]);

  useEffect(() => {
    if (activeTab !== 'messages') return;
    if (activeConversationId) return;
    const firstConversationId = (conversations || [])[0]?.id;
    if (!firstConversationId) return;
    openConversationById(firstConversationId).catch(() => {
      setMessageActionError('Unable to load the first conversation');
    });
  }, [activeTab, activeConversationId, conversations]);

  const handleTaskStatusChange = async (taskId, status) => {
    setError('');
    try {
      const response = await axios.patch(`${API}/employee/tasks/${taskId}/status`, { status });
      const updatedTask = response.data?.task;
      setTasks((current) => current.map((task) => (task.id === taskId ? { ...task, ...(updatedTask || {}), status } : task)));
      setFeedback('Task status updated');
      await Promise.all([loadDashboard(), loadTasks()]);
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'Unable to update task status'));
    }
  };

  const handleSaveTaskNote = async (taskId) => {
    setError('');
    try {
      const note = noteDraftByTask[taskId] ?? '';
      const response = await axios.patch(`${API}/employee/tasks/${taskId}`, { note });
      const updatedTask = response.data?.task;
      setTasks((current) => current.map((task) => (task.id === taskId ? { ...task, ...(updatedTask || {}) } : task)));
      setFeedback('Task note updated');
      await Promise.all([loadDashboard(), loadTasks()]);
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'Unable to save task note'));
    }
  };

  const handleAddTaskComment = async (taskId) => {
    const message = String(commentDraftByTask[taskId] || '').trim();
    if (!message) {
      setError('Comment cannot be empty');
      return;
    }
    setError('');
    try {
      const response = await axios.patch(`${API}/employee/tasks/${taskId}`, { comment: message });
      const updatedTask = response.data?.task;
      setTasks((current) => current.map((task) => (task.id === taskId ? { ...task, ...(updatedTask || {}) } : task)));
      setCommentDraftByTask((current) => ({ ...current, [taskId]: '' }));
      setFeedback('Comment added');
      await Promise.all([loadDashboard(), loadTasks()]);
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'Unable to add comment'));
    }
  };

  const handleTaskFileUpload = async (taskId, file) => {
    if (!file) return;
    setUploadingTaskId(taskId);
    setError('');
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      const uploadResponse = await axios.post(`${API}/employee/tasks/${taskId}/attachments/upload`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const updatedTask = uploadResponse.data?.task;
      setTasks((current) => current.map((task) => (task.id === taskId ? { ...task, ...(updatedTask || {}) } : task)));
      setFeedback('Task file uploaded');
      await Promise.all([loadDashboard(), loadTasks()]);
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'Unable to upload task file'));
    } finally {
      setUploadingTaskId('');
    }
  };

  const handleConversationUserSearch = async () => {
    setMessageActionError('');
    try {
      await loadConversationUsers(conversationSearch, conversationDepartmentFilter);
    } catch (requestError) {
      setMessageActionError(getErrorMessage(requestError, 'Unable to search employees'));
    }
  };

  const openConversationById = async (conversationId) => {
    if (!conversationId) return;
    setMessageActionError('');
    try {
      await loadConversationDetail(conversationId);
      await Promise.all([loadConversations(conversationSearch), loadDashboard()]);
    } catch (requestError) {
      setMessageActionError(getErrorMessage(requestError, 'Unable to open conversation'));
    }
  };

  const handleDownloadAttachment = (attachment, preview = false) => {
    const href = getAttachmentHref(attachment, preview);
    if (!href) return;
    const token = localStorage.getItem('token');
    const separator = href.includes('?') ? '&' : '?';
    const url = token ? `${href}${separator}access_token=${encodeURIComponent(token)}` : href;
    if (preview) {
      // Preview: open inline in a new tab (server returns Content-Disposition: inline)
      window.open(url, '_blank', 'noreferrer noopener');
    } else {
      // Download: hidden iframe — the server's Content-Disposition: attachment header
      // causes the browser to save the file without navigating or opening a new tab.
      // This is the most reliable cross-origin download technique.
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;';
      iframe.src = url;
      document.body.appendChild(iframe);
      setTimeout(() => {
        if (document.body.contains(iframe)) document.body.removeChild(iframe);
      }, 30000);
    }
  };

  const handleUploadConversationAttachments = async (files) => {
    const selectedFiles = Array.from(files || []).filter(Boolean);
    if (selectedFiles.length === 0) return;
    setConversationAttachmentUploading(true);
    setConversationUploadProgress(0);
    setMessageActionError('');
    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => formData.append('files', file));
      if (activeConversationId) {
        formData.append('conversation_id', activeConversationId);
      }
      const response = await axios.post(`${API}/employee/conversations/attachments/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (event) => {
          const total = Number(event.total || 0);
          const loaded = Number(event.loaded || 0);
          if (total > 0) {
            setConversationUploadProgress(Math.round((loaded / total) * 100));
          }
        },
      });
      const uploaded = response.data?.attachments || [];
      setConversationAttachmentDraft((current) => [...current, ...uploaded]);
      setMessageActionSuccess(`File uploaded successfully (${uploaded.length})`);
    } catch (requestError) {
      setMessageActionError(getErrorMessage(requestError, 'Upload failed. Please try again.'));
    } finally {
      setConversationAttachmentUploading(false);
      setConversationUploadProgress(0);
    }
  };

  const handleRemoveConversationAttachmentDraft = (attachmentId) => {
    setConversationAttachmentDraft((current) => current.filter((item) => item.id !== attachmentId));
  };

  const handleStartConversation = async () => {
    if (!newConversationUserId) {
      setMessageActionError('Please choose an employee/admin to start a conversation.');
      return;
    }
    setMessageActionError('');
    try {
      const response = await axios.post(`${API}/employee/conversations`, {
        participant_ids: [newConversationUserId],
        title: String(newConversationTitle || '').trim() || undefined,
      });
      const conversation = response.data?.conversation;
      setNewConversationTitle('');
      setNewConversationUserId('');
      setMessageActionSuccess('Conversation started successfully.');
      await loadConversations(conversationSearch);
      if (conversation?.id) {
        await openConversationById(conversation.id);
      }
    } catch (requestError) {
      setMessageActionError(getErrorMessage(requestError, 'Unable to start conversation'));
    }
  };

  const handleSendConversationMessage = async () => {
    if (!activeConversationId) {
      setMessageActionError('Please select or create a conversation first.');
      return;
    }
    const text = String(conversationMessageDraft || '').trim();
    const attachmentIds = (conversationAttachmentDraft || []).map((item) => item.id).filter(Boolean);
    if (!text && attachmentIds.length === 0) {
      setMessageActionError('Type a message or upload at least one file.');
      return;
    }

    setMessageActionError('');
    try {
      await axios.post(`${API}/employee/conversations/${activeConversationId}/messages`, {
        message: text,
        attachment_ids: attachmentIds,
      });
      setConversationMessageDraft('');
      setConversationAttachmentDraft([]);
      setMessageActionSuccess('Message sent successfully.');
      await Promise.all([openConversationById(activeConversationId), loadDashboard()]);
    } catch (requestError) {
      setMessageActionError(getErrorMessage(requestError, 'Unable to send message'));
    }
  };

  const handleSaveEditedMessage = async (messageId) => {
    const text = String(editingMessageText || '').trim();
    if (!text) {
      setMessageActionError('Validation failed: message text cannot be empty.');
      return;
    }
    setMessageActionError('');
    try {
      await axios.patch(`${API}/employee/conversations/${activeConversationId}/messages/${messageId}`, { message: text });
      setEditingMessageId('');
      setEditingMessageText('');
      setMessageActionSuccess('Changes saved successfully.');
      await openConversationById(activeConversationId);
    } catch (requestError) {
      setMessageActionError(getErrorMessage(requestError, 'Unable to update message'));
    }
  };

  const handleDeleteConversationMessage = async (messageId) => {
    setMessageActionError('');
    try {
      await axios.delete(`${API}/employee/conversations/${activeConversationId}/messages/${messageId}`);
      setMessageActionSuccess('Item deleted successfully.');
      await openConversationById(activeConversationId);
    } catch (requestError) {
      setMessageActionError(getErrorMessage(requestError, 'Unable to delete message'));
    }
  };

  const handleNotificationClick = async (notification) => {
    const link = notification?.link;
    try {
      if (notification?.id && !notification.is_read) {
        await axios.post(`${API}/notifications/${notification.id}/read`);
      }
    } catch (_) {
      // Ignore read errors so navigation still works.
    }

    if (!link) return;
    const params = link.params || {};
    if (link.page === 'employee-dashboard') {
      if (params.tab) setActiveTab(params.tab);
      if (params.conversation_id) {
        await openConversationById(params.conversation_id);
      }
      await loadDashboard();
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    setError('');
    try {
      const response = await axios.put(`${API}/employee/profile`, profileForm);
      const updatedProfile = response.data?.profile || null;
      setDashboard((current) => (current ? { ...current, profile: updatedProfile || current.profile } : current));
      setFeedback('Profile updated');
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'Unable to update profile'));
    } finally {
      setSavingProfile(false);
    }
  };

  const handleProfilePhotoUpload = async (file) => {
    if (!file) return;
    setPhotoUploading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('profile_picture', file);
      const response = await axios.post(`${API}/employee/profile/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const updatedProfile = response.data?.profile || null;
      setDashboard((current) => (current ? { ...current, profile: updatedProfile || current.profile } : current));
      setFeedback('Profile photo updated');
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'Unable to upload profile photo'));
    } finally {
      setPhotoUploading(false);
    }
  };

  const unreadNotifications = useMemo(
    () => (dashboard?.notifications || []).filter((entry) => !entry.is_read),
    [dashboard]
  );
  const sortedNotifications = useMemo(() => sortNotificationsByDateDesc(dashboard?.notifications || []), [dashboard]);
  const groupedNotificationFeed = useMemo(() => groupByDateLabel(sortedNotifications), [sortedNotifications]);
  const groupedConversationMessages = useMemo(() => groupByDateLabel(conversationMessages || []), [conversationMessages]);
  const conversationCandidates = useMemo(() => {
    const lowered = conversationSearch.trim().toLowerCase();
    if (!lowered) return conversationUsers;
    return (conversationUsers || []).filter((item) => {
      const haystack = [item.name, item.email, item.department, item.position].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(lowered);
    });
  }, [conversationUsers, conversationSearch]);
  const activeTasks = useMemo(() => tasks.filter((task) => String(task.status || '').toLowerCase() !== 'completed'), [tasks]);
  const completedTasks = useMemo(() => tasks.filter((task) => String(task.status || '').toLowerCase() === 'completed'), [tasks]);

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="border-b border-yellow-400/20 bg-gradient-to-r from-gray-950 via-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-full bg-gradient-to-r from-yellow-500 to-yellow-600 flex items-center justify-center text-black font-bold text-xl overflow-hidden">
              {profile.profile_photo_url ? (
                <img src={resolveImageUrl(profile.profile_photo_url)} alt="Profile" className="h-full w-full object-cover" />
              ) : (
                (profile.firstname || profile.name || 'E').charAt(0).toUpperCase()
              )}
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-yellow-400">Employee Workspace</div>
              <div className="text-2xl font-bold">{profile.name || 'Employee'}</div>
              <div className="text-sm text-gray-400">
                {profile.position || 'Employee'}{profile.department ? ` • ${profile.department}` : ''}{profile.employee_id ? ` • ${profile.employee_id}` : ''}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-wide text-gray-400">Unread alerts</div>
            <div className="text-2xl font-bold text-yellow-400">{unreadNotifications.length}</div>
          </div>
        </div>
      </div>

      {(error || feedback) && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          {error && <div className="mb-2 p-3 rounded-lg bg-red-900/60 text-red-200 border border-red-500/30">{error}</div>}
          {feedback && <div className="mb-2 p-3 rounded-lg bg-green-900/40 text-green-200 border border-green-500/30">{feedback}</div>}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
        <aside className="space-y-2">
          {NAV_SECTIONS.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setActiveTab(item.key)}
              className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all border ${
                activeTab === item.key
                  ? 'border-yellow-400 bg-yellow-400/10 text-yellow-300'
                  : 'border-transparent text-gray-300 hover:border-yellow-400/30 hover:bg-gray-900'
              }`}
            >
              {item.label}
            </button>
          ))}
        </aside>

        <main>
          {loading ? (
            <div className="text-gray-400">Loading your workspace...</div>
          ) : (
            <>
              {activeTab === 'dashboard' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="rounded-2xl border border-yellow-400/20 bg-gray-900 p-5">
                      <div className="text-3xl font-bold text-yellow-400">{stats.pending_tasks ?? 0}</div>
                      <div className="text-sm text-gray-400 mt-1">Pending Tasks</div>
                    </div>
                    <div className="rounded-2xl border border-yellow-400/20 bg-gray-900 p-5">
                      <div className="text-3xl font-bold text-yellow-400">{stats.in_progress_tasks ?? 0}</div>
                      <div className="text-sm text-gray-400 mt-1">In Progress</div>
                    </div>
                    <div className="rounded-2xl border border-yellow-400/20 bg-gray-900 p-5">
                      <div className="text-3xl font-bold text-yellow-400">{stats.completed_tasks ?? 0}</div>
                      <div className="text-sm text-gray-400 mt-1">Completed</div>
                    </div>
                    <div className="rounded-2xl border border-yellow-400/20 bg-gray-900 p-5">
                      <div className="text-3xl font-bold text-yellow-400">{stats.tasks_today ?? 0}</div>
                      <div className="text-sm text-gray-400 mt-1">Due Today</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                    <div className="rounded-2xl border border-yellow-400/20 bg-gray-900 p-5">
                      <h3 className="text-lg font-bold text-yellow-400 mb-3">Priority Work Queue</h3>
                      {(dashboard?.tasks || []).length === 0 ? (
                        <div className="text-sm text-gray-400">No pending tasks. You are all caught up.</div>
                      ) : (
                        <div className="space-y-3">
                          {(dashboard?.tasks || []).map((task) => (
                            <div key={task.id} className="border border-gray-800 rounded-xl p-3">
                              <div className="flex items-center justify-between gap-2">
                                <div className="font-semibold text-white text-sm">{task.title}</div>
                                <span className={`text-xs px-2 py-0.5 rounded-full border ${PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.medium}`}>{task.priority}</span>
                              </div>
                              <div className="text-xs text-gray-400 mt-1">Due {formatDate(task.deadline)}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="rounded-2xl border border-yellow-400/20 bg-gray-900 p-5">
                      <h3 className="text-lg font-bold text-yellow-400 mb-3">Latest Notifications</h3>
                      {(dashboard?.notifications || []).length === 0 ? (
                        <div className="text-sm text-gray-400">No notifications yet.</div>
                      ) : (
                        <div className="space-y-2">
                          {(dashboard?.notifications || []).slice(0, 8).map((entry) => (
                            <button
                              type="button"
                              onClick={() => handleNotificationClick(entry)}
                              key={entry.id}
                              className={`w-full text-left border rounded-xl p-3 transition-all ${entry.link ? 'hover:border-yellow-400 cursor-pointer' : ''} ${entry.is_read ? 'border-gray-800 bg-gray-950' : 'border-yellow-400/40 bg-yellow-400/5'}`}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="text-sm font-medium text-white">{entry.title || 'Notification'}</div>
                                {!entry.is_read && <span className="text-[10px] bg-yellow-500 text-black px-2 py-0.5 rounded-full font-bold">New</span>}
                              </div>
                              <div className="text-xs text-gray-400 mt-1">{entry.message}</div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'tasks' && (
                <div className="rounded-2xl border border-yellow-400/20 bg-gray-900 p-5 space-y-4">
                  <h3 className="text-lg font-bold text-yellow-400">Assigned Tasks</h3>
                  {tasks.length === 0 ? (
                    <div className="text-sm text-gray-400">No tasks assigned yet.</div>
                  ) : (
                    <>
                      <div>
                        <div className="text-sm font-semibold text-gray-300 mb-3">Active Tasks</div>
                        <div className="space-y-4">
                          {activeTasks.length === 0 ? <div className="text-sm text-gray-500">No active tasks.</div> : activeTasks.map((task) => (
                      <div key={task.id} className="border border-gray-800 rounded-xl p-4 space-y-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <div className="font-semibold text-white">{task.title}</div>
                            <div className="text-xs text-gray-400 mt-1">Assigned by {task.assigned_by_name || 'Admin'} • Start {formatDate(task.start_date)} • Due {formatDate(task.deadline)}</div>
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full border ${PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.medium}`}>{task.priority}</span>
                        </div>

                        {task.description && <p className="text-sm text-gray-300">{task.description}</p>}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Status</label>
                            <select
                              value={task.status || 'pending'}
                              onChange={(event) => handleTaskStatusChange(task.id, event.target.value)}
                              className="w-full bg-gray-800 text-white text-sm rounded-lg border border-gray-600 px-3 py-2 focus:border-yellow-400 focus:outline-none"
                            >
                              {STATUS_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Upload File</label>
                            <input
                              type="file"
                              onChange={(event) => handleTaskFileUpload(task.id, event.target.files?.[0])}
                              className="w-full text-xs text-gray-300 file:mr-3 file:rounded-md file:border-0 file:bg-yellow-500 file:px-3 file:py-2 file:text-xs file:font-semibold file:text-black hover:file:bg-yellow-400"
                            />
                            {uploadingTaskId === task.id && <div className="text-xs text-gray-400 mt-1">Uploading...</div>}
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Work Note</label>
                          <textarea
                            value={noteDraftByTask[task.id] ?? task.employee_note ?? ''}
                            onChange={(event) => setNoteDraftByTask((current) => ({ ...current, [task.id]: event.target.value }))}
                            rows={3}
                            className="w-full bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none text-sm"
                            placeholder="Add progress details, blockers, or completion notes"
                          />
                          <div className="mt-2">
                            <button onClick={() => handleSaveTaskNote(task.id)} className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded-lg text-sm font-semibold">Save Note</button>
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Comments</label>
                          <div className="space-y-2 mb-2">
                            {(task.employee_comments || []).slice(-3).map((comment) => (
                              <div key={comment.id} className="bg-gray-950 border border-gray-800 rounded-lg p-2 text-xs text-gray-300">
                                <span className="text-yellow-400 font-semibold">{comment.author_name || 'Employee'}:</span> {comment.message}
                              </div>
                            ))}
                          </div>
                          <div className="flex flex-col md:flex-row gap-2">
                            <input
                              type="text"
                              value={commentDraftByTask[task.id] || ''}
                              onChange={(event) => setCommentDraftByTask((current) => ({ ...current, [task.id]: event.target.value }))}
                              className="flex-1 bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none text-sm"
                              placeholder="Add a comment"
                            />
                            <button onClick={() => handleAddTaskComment(task.id)} className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-semibold">Post</button>
                          </div>
                        </div>

                        {(task.attachments || []).length > 0 && (
                          <div>
                            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Attachments</div>
                            <div className="space-y-2">
                              {(task.attachments || []).map((attachment, index) => (
                                <div key={`${task.id}-attachment-${attachment.id || index}`} className="border border-gray-800 rounded-lg p-2 bg-gray-950">
                                  <div className="flex flex-wrap items-start justify-between gap-2">
                                    <div className="min-w-0">
                                      <div className="text-xs text-yellow-300 font-semibold uppercase tracking-wide">{getAttachmentTypeLabel(attachment)}</div>
                                      <div className="text-sm text-white truncate">{attachment.filename || `File ${index + 1}`}</div>
                                      <div className="text-[11px] text-gray-400 mt-1">{formatAttachmentSize(attachment.size)} • Uploaded {formatAttachmentDate(attachment.uploaded_at)}</div>
                                    </div>
                                    <div className="flex gap-2">
                                      {isImageAttachment(attachment) && getAttachmentHref(attachment, true) && (
                                        <button
                                          type="button"
                                          onClick={() => handleDownloadAttachment(attachment, true)}
                                          className="text-xs px-2 py-1 rounded-md border border-gray-700 text-gray-200 hover:border-yellow-400"
                                        >
                                          Preview
                                        </button>
                                      )}
                                      {getAttachmentHref(attachment, false) && (
                                        <button
                                          type="button"
                                          onClick={() => handleDownloadAttachment(attachment, false)}
                                          className="text-xs px-2 py-1 rounded-md border border-gray-700 text-yellow-300 hover:border-yellow-400"
                                        >
                                          Download
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                          ))}
                        </div>
                      </div>

                      <div className="pt-2 border-t border-gray-800">
                        <div className="text-sm font-semibold text-gray-300 mb-3">Completed Task History</div>
                        <div className="space-y-3">
                          {completedTasks.length === 0 ? <div className="text-sm text-gray-500">No completed tasks yet.</div> : completedTasks.map((task) => (
                            <div key={`completed-${task.id}`} className="border border-green-700/40 bg-green-900/10 rounded-xl p-3">
                              <div className="text-white font-medium text-sm">{task.title}</div>
                              <div className="text-xs text-gray-400 mt-1">Started {formatDate(task.start_date)} • Completed {formatDate(task.completed_at)} • Due {formatDate(task.deadline)}</div>
                              {task.employee_note && <div className="text-xs text-gray-300 mt-1">Final note: {task.employee_note}</div>}
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {activeTab === 'meetings' && (
                <div className="rounded-2xl border border-yellow-400/20 bg-gray-900 p-5 space-y-4">
                  <h3 className="text-lg font-bold text-yellow-400">My Meetings</h3>
                  {meetings.length === 0 ? (
                    <div className="text-sm text-gray-400">No meetings scheduled yet.</div>
                  ) : (
                    <div className="space-y-3">
                      {meetings.map((meeting) => (
                        <div key={meeting.id} className="border border-gray-800 rounded-xl p-4">
                          <div className="flex items-center justify-between gap-2">
                            <div className="font-semibold text-white">{meeting.title}</div>
                            <div className="text-xs text-yellow-300">{new Date(meeting.meeting_at).toLocaleString()}</div>
                          </div>
                          <div className="text-xs text-gray-400 mt-1">Platform: {meeting.platform}</div>
                          {meeting.description && <div className="text-sm text-gray-300 mt-2">{meeting.description}</div>}
                          <a href={meeting.meeting_url} target="_blank" rel="noreferrer" className="inline-block mt-3 text-sm text-yellow-300 hover:text-yellow-200">Join meeting link</a>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'messages' && (
                <div className="rounded-2xl border border-yellow-400/20 bg-gray-900 p-4 lg:p-5">
                  <div className="grid grid-cols-1 xl:grid-cols-[320px_1fr] gap-4">
                    <div className="space-y-4">
                      <div className="border border-gray-800 rounded-xl p-4 bg-gray-950/60 space-y-3">
                        <h3 className="text-base font-bold text-yellow-400">Start Conversation</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <input
                            value={conversationSearch}
                            onChange={(event) => setConversationSearch(event.target.value)}
                            className="bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-600 text-sm"
                            placeholder="Search name"
                          />
                          <input
                            value={conversationDepartmentFilter}
                            onChange={(event) => setConversationDepartmentFilter(event.target.value)}
                            className="bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-600 text-sm"
                            placeholder="Department"
                          />
                        </div>
                        <button onClick={handleConversationUserSearch} className="w-full bg-gray-800 hover:bg-gray-700 text-white px-3 py-2 rounded-lg text-sm border border-gray-600">Find Teammates</button>
                        <select
                          value={newConversationUserId}
                          onChange={(event) => setNewConversationUserId(event.target.value)}
                          className={`w-full bg-gray-800 text-white px-3 py-2 rounded-lg border text-sm ${!newConversationUserId && messageActionError ? 'border-red-500' : 'border-gray-600'}`}
                        >
                          <option value="">Select employee or admin</option>
                          {conversationCandidates.map((person) => (
                            <option key={person.id} value={person.id}>
                              {person.name} ({person.role}{person.department ? ` • ${person.department}` : ''})
                            </option>
                          ))}
                        </select>
                        <input
                          value={newConversationTitle}
                          onChange={(event) => setNewConversationTitle(event.target.value)}
                          className="w-full bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-600 text-sm"
                          placeholder="Optional conversation title"
                        />
                        <button onClick={handleStartConversation} className="w-full bg-yellow-500 hover:bg-yellow-600 text-black px-3 py-2 rounded-lg text-sm font-semibold">Start Chat</button>
                      </div>

                      <div className="border border-gray-800 rounded-xl p-3 bg-gray-950/60">
                        <div className="text-sm text-gray-300 font-semibold mb-2">Conversations</div>
                        <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
                          {(conversations || []).length === 0 ? (
                            <div className="text-sm text-gray-500">No conversations yet.</div>
                          ) : (
                            (conversations || []).map((thread) => (
                              <button
                                type="button"
                                key={thread.id}
                                onClick={() => openConversationById(thread.id)}
                                className={`w-full text-left rounded-lg border p-3 transition-colors ${activeConversationId === thread.id ? 'border-yellow-400 bg-yellow-400/10' : 'border-gray-700 bg-gray-900 hover:border-yellow-500/50'}`}
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="text-sm font-semibold text-white line-clamp-1">{thread.display_name || thread.title || 'Conversation'}</div>
                                  {thread.unread_count > 0 && <span className="text-[11px] bg-yellow-500 text-black px-2 py-0.5 rounded-full font-bold">{thread.unread_count}</span>}
                                </div>
                                <div className="text-[10px] uppercase tracking-wide text-yellow-300 mt-2">Subject</div>
                                <div className="text-xs text-gray-200 line-clamp-1">{thread.subject || thread.subject_display || 'No Subject'}</div>
                                <div className="text-xs text-gray-400 mt-1 line-clamp-2">{thread.last_message_preview || 'No messages yet'}</div>
                                <div className="text-[11px] text-gray-500 mt-1">{formatDateTime(thread.last_message_at || thread.updated_at)}</div>
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="border border-gray-800 rounded-xl bg-gray-950/60 p-4 flex flex-col min-h-[540px]">
                      <div className="pb-3 border-b border-gray-800">
                        <div className="text-lg font-bold text-yellow-400">{activeConversation?.display_name || activeConversation?.title || 'Select a Conversation'}</div>
                        <div className="text-[10px] uppercase tracking-wide text-yellow-300 mt-2">Subject</div>
                        <div className="text-sm text-gray-200">{activeConversation?.subject || activeConversation?.subject_display || 'No Subject'}</div>
                        {activeConversation?.participant_profiles?.length > 0 && (
                          <div className="text-xs text-gray-400 mt-1">
                            {activeConversation.participant_profiles.map((person) => person.name || person.email).join(', ')}
                          </div>
                        )}
                      </div>

                      <div className="overflow-y-auto py-3 pr-1 space-y-3 max-h-[380px] min-h-[220px]">
                        {!activeConversationId ? (
                          <div className="text-sm text-gray-500">Choose a conversation from the left panel to begin.</div>
                        ) : conversationLoading ? (
                          <div className="text-sm text-gray-500">Loading conversation...</div>
                        ) : groupedConversationMessages.length === 0 ? (
                          <div className="text-sm text-gray-500">No messages yet. Say hello.</div>
                        ) : (
                          groupedConversationMessages.map((entry) => {
                            if (entry.type === 'separator') {
                              return (
                                <div key={entry.id} className="text-center text-[11px] text-gray-500 uppercase tracking-wide">{entry.label}</div>
                              );
                            }

                            const message = entry.value;
                            const mine = message.sender_id === profile.id;
                            return (
                              <div key={message.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[92%] md:max-w-[78%] rounded-2xl border p-3.5 shadow-sm ${mine ? 'bg-gradient-to-br from-yellow-500/15 to-yellow-400/5 border-yellow-500/45 shadow-yellow-500/10' : 'bg-gradient-to-br from-gray-900 to-gray-900/70 border-gray-700 shadow-black/30'}`}>
                                  <div className="flex items-center justify-between gap-3">
                                    <div className="text-xs font-semibold text-yellow-300">{mine ? 'You' : (message.sender_name || 'Teammate')}</div>
                                    <div className="text-[11px] text-gray-400">{formatDateTime(message.created_at)}</div>
                                  </div>

                                  {editingMessageId === message.id ? (
                                    <div className="mt-2 space-y-2">
                                      <textarea
                                        value={editingMessageText}
                                        onChange={(event) => setEditingMessageText(event.target.value)}
                                        rows={3}
                                        className="w-full bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-600 text-sm"
                                      />
                                      <div className="flex gap-2">
                                        <button onClick={() => handleSaveEditedMessage(message.id)} className="bg-yellow-500 hover:bg-yellow-600 text-black px-3 py-1.5 rounded text-xs font-semibold">Save</button>
                                        <button onClick={() => { setEditingMessageId(''); setEditingMessageText(''); }} className="bg-gray-800 hover:bg-gray-700 text-white px-3 py-1.5 rounded text-xs border border-gray-600">Cancel</button>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      {message.message && <div className="text-sm text-gray-100 mt-2 whitespace-pre-wrap">{message.message}</div>}
                                      {(message.attachments || []).length > 0 && (
                                        <div className="mt-3 space-y-2">
                                          {(message.attachments || []).map((attachment) => (
                                            <div key={attachment.id} className="border border-gray-700/80 rounded-xl p-2.5 bg-black/25">
                                              <div className="flex flex-wrap items-start justify-between gap-2">
                                                <div className="min-w-0">
                                                  <div className="text-xs text-yellow-300 font-semibold uppercase tracking-wide">{getAttachmentTypeLabel(attachment)}</div>
                                                  <div className="text-sm text-white truncate">{attachment.filename}</div>
                                                  <div className="text-[11px] text-gray-400">{formatAttachmentSize(attachment.size)} • {formatAttachmentDate(attachment.uploaded_at)}</div>
                                                </div>
                                                <div className="flex gap-2">
                                                  {(isImageAttachment(attachment) || isVideoAttachment(attachment)) && getAttachmentHref(attachment, true) && (
                                                    <button type="button" onClick={() => handleDownloadAttachment(attachment, true)} className="text-xs px-2 py-1 rounded border border-gray-700 text-gray-100 hover:border-yellow-400">Preview</button>
                                                  )}
                                                  {getAttachmentHref(attachment, false) && (
                                                    <button type="button" onClick={() => handleDownloadAttachment(attachment, false)} className="text-xs px-2 py-1 rounded border border-gray-700 text-yellow-300 hover:border-yellow-400">Download</button>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      <div className="mt-2 flex items-center justify-between gap-3 text-[11px] text-gray-400">
                                        <span>{message.edited_at ? `Edited ${formatDateTime(message.edited_at)}` : 'Delivered'}</span>
                                        {mine && (
                                          <span>{message.read_count > 1 ? `Read by ${message.read_count - 1}` : 'Sent'}</span>
                                        )}
                                      </div>
                                      {mine && (
                                        <div className="mt-2.5 flex gap-2">
                                          <button onClick={() => { setEditingMessageId(message.id); setEditingMessageText(message.message || ''); }} className="text-[11px] px-2 py-1 rounded border border-gray-600 text-gray-200 hover:border-yellow-400">Edit</button>
                                          <button onClick={() => handleDeleteConversationMessage(message.id)} className="text-[11px] px-2 py-1 rounded border border-red-600 text-red-300 hover:bg-red-900/30">Delete</button>
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>

                      <div className="pt-2.5 border-t border-gray-800 space-y-3">
                        <textarea
                          value={conversationMessageDraft}
                          onChange={(event) => setConversationMessageDraft(event.target.value)}
                          rows={3}
                          className={`w-full bg-gray-800 text-white px-3 py-2 rounded-lg border ${messageActionError && !activeConversationId ? 'border-red-500' : 'border-gray-600'} text-sm`}
                          placeholder={activeConversationId ? 'Type a message...' : 'Select a conversation to compose'}
                        />

                        <div>
                          <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Attachments</label>
                          <input
                            type="file"
                            multiple
                            onChange={(event) => handleUploadConversationAttachments(event.target.files)}
                            className="w-full text-xs text-gray-300 file:mr-3 file:rounded-md file:border-0 file:bg-yellow-500 file:px-3 file:py-2 file:text-xs file:font-semibold file:text-black hover:file:bg-yellow-400"
                          />
                          {conversationAttachmentUploading && (
                            <div className="text-xs text-gray-400 mt-1">Uploading... {conversationUploadProgress}%</div>
                          )}
                        </div>

                        {(conversationAttachmentDraft || []).length > 0 && (
                          <div className="space-y-2 max-h-32 overflow-y-auto pr-1">
                            {conversationAttachmentDraft.map((attachment) => (
                              <div key={attachment.id} className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <div className="text-sm text-white truncate">{attachment.filename}</div>
                                  <div className="text-[11px] text-gray-400">{formatAttachmentSize(attachment.size)} • {getAttachmentTypeLabel(attachment)}</div>
                                </div>
                                <button type="button" onClick={() => handleRemoveConversationAttachmentDraft(attachment.id)} className="text-[11px] px-2 py-1 rounded border border-red-600 text-red-300 hover:bg-red-900/30">Remove</button>
                              </div>
                            ))}
                          </div>
                        )}

                        {messageActionSuccess && <div className="rounded-lg border border-green-500/40 bg-green-900/30 text-green-200 px-3 py-2 text-sm">{messageActionSuccess}</div>}
                        {messageActionError && <div className="rounded-lg border border-red-500/40 bg-red-900/40 text-red-200 px-3 py-2 text-sm">{messageActionError}</div>}

                        <div className="flex justify-end">
                          <button
                            onClick={handleSendConversationMessage}
                            disabled={!activeConversationId}
                            className="bg-yellow-500 hover:bg-yellow-600 disabled:opacity-60 disabled:cursor-not-allowed text-black px-5 py-2 rounded-lg font-semibold"
                          >
                            Send Message
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'announcements' && (
                <div className="space-y-6">
                  <div className="rounded-2xl border border-yellow-400/20 bg-gray-900 p-5">
                    <h3 className="text-lg font-bold text-yellow-400 mb-4">Announcements</h3>
                    {(dashboard?.announcements || []).length === 0 ? (
                      <div className="text-sm text-gray-400">No announcements right now.</div>
                    ) : (
                      <div className="space-y-3">
                        {(dashboard?.announcements || []).map((entry) => (
                          <div key={entry.id} className="border border-gray-800 rounded-xl p-4">
                            <div className="font-semibold text-white">{entry.title}</div>
                            <div className="text-sm text-gray-300 mt-1">{entry.content || entry.message}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="rounded-2xl border border-yellow-400/20 bg-gray-900 p-5">
                    <h3 className="text-lg font-bold text-yellow-400 mb-4">Notification Center</h3>
                    {groupedNotificationFeed.length === 0 ? (
                      <div className="text-sm text-gray-400">No notifications right now.</div>
                    ) : (
                      <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                        {groupedNotificationFeed.map((entry) => {
                          if (entry.type === 'separator') {
                            return <div key={entry.id} className="text-[11px] uppercase tracking-wide text-gray-500 text-center py-1">{entry.label}</div>;
                          }
                          const item = entry.value;
                          return (
                            <button
                              key={item.id}
                              type="button"
                              onClick={() => handleNotificationClick(item)}
                              className={`w-full text-left border rounded-xl p-3 transition-all ${item.link ? 'hover:border-yellow-400 cursor-pointer' : ''} ${item.is_read ? 'border-gray-800 bg-gray-950' : 'border-yellow-500/40 bg-yellow-500/5'}`}
                            >
                              <div className="flex items-center justify-between gap-3">
                                <div className="text-sm font-medium text-white">{item.title || 'Notification'}</div>
                                {!item.is_read && <span className="text-[10px] bg-yellow-500 text-black px-2 py-0.5 rounded-full font-bold">New</span>}
                              </div>
                              <div className="text-xs text-gray-300 mt-1">{item.message}</div>
                              <div className="text-[11px] text-gray-500 mt-2">{formatDateTime(item.created_at)}</div>
                              {item.link && <div className="text-[11px] text-yellow-300 mt-1">Open related item →</div>}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'profile' && (
                <div className="rounded-2xl border border-yellow-400/20 bg-gray-900 p-6 max-w-4xl">
                  <h3 className="text-lg font-bold text-yellow-400 mb-4">Profile Information</h3>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
                    <div className="h-24 w-24 rounded-full overflow-hidden bg-gray-800 border border-yellow-400/20 flex items-center justify-center text-2xl font-bold text-yellow-300">
                      {profile.profile_photo_url ? (
                        <img src={resolveImageUrl(profile.profile_photo_url)} alt="Profile" className="h-full w-full object-cover" />
                      ) : (
                        (profile.firstname || profile.name || 'E').charAt(0).toUpperCase()
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Profile Photo</label>
                      <input
                        type="file"
                        accept="image/*"
                        disabled={photoUploading}
                        onChange={(event) => {
                          const nextFile = event.target.files?.[0];
                          handleProfilePhotoUpload(nextFile);
                          event.target.value = '';
                        }}
                        className="block w-full max-w-sm text-sm text-gray-300 file:mr-4 file:rounded-lg file:border-0 file:bg-yellow-500 file:px-4 file:py-2 file:font-semibold file:text-black hover:file:bg-yellow-400 disabled:opacity-60"
                      />
                      <div className="mt-2 text-xs text-gray-400">
                        {photoUploading ? 'Uploading image...' : 'Upload JPG, PNG, GIF, or WEBP up to 5MB.'}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4 text-sm">
                    <div><span className="text-gray-400">Name:</span> <span className="text-white font-medium">{profile.name || `${profile.firstname || ''} ${profile.lastname || ''}`.trim()}</span></div>
                    <div><span className="text-gray-400">Email:</span> <span className="text-white font-medium">{profile.email}</span></div>
                    <div><span className="text-gray-400">Employee ID:</span> <span className="text-white font-medium">{profile.employee_id || '—'}</span></div>
                    <div><span className="text-gray-400">Position:</span> <span className="text-white font-medium">{profile.position || '—'}</span></div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input value={profileForm.phone_number} onChange={(event) => setProfileForm((current) => ({ ...current, phone_number: event.target.value }))} className="bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600" placeholder="Phone Number" />
                    <input value={profileForm.country} onChange={(event) => setProfileForm((current) => ({ ...current, country: event.target.value }))} className="bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600" placeholder="Country" />
                    <input value={profileForm.address} onChange={(event) => setProfileForm((current) => ({ ...current, address: event.target.value }))} className="bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 md:col-span-2" placeholder="Address" />
                    <input value={profileForm.city} onChange={(event) => setProfileForm((current) => ({ ...current, city: event.target.value }))} className="bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600" placeholder="City" />
                    <input value={profileForm.province_state} onChange={(event) => setProfileForm((current) => ({ ...current, province_state: event.target.value }))} className="bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600" placeholder="Province / State" />
                    <input value={profileForm.postal_code} onChange={(event) => setProfileForm((current) => ({ ...current, postal_code: event.target.value }))} className="bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600" placeholder="Postal Code" />
                    <textarea value={profileForm.bio} onChange={(event) => setProfileForm((current) => ({ ...current, bio: event.target.value }))} rows={3} className="bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 md:col-span-2" placeholder="Short Bio" />
                    <input value={profileForm.emergency_contact_name} onChange={(event) => setProfileForm((current) => ({ ...current, emergency_contact_name: event.target.value }))} className="bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600" placeholder="Emergency Contact Name" />
                    <input value={profileForm.emergency_contact_relationship} onChange={(event) => setProfileForm((current) => ({ ...current, emergency_contact_relationship: event.target.value }))} className="bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600" placeholder="Emergency Contact Relationship" />
                    <input value={profileForm.emergency_contact_phone} onChange={(event) => setProfileForm((current) => ({ ...current, emergency_contact_phone: event.target.value }))} className="bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 md:col-span-2" placeholder="Emergency Contact Phone" />
                  </div>

                  <div className="mt-4">
                    <button onClick={handleSaveProfile} disabled={savingProfile} className="bg-yellow-500 hover:bg-yellow-600 disabled:opacity-60 text-black px-6 py-3 rounded-lg font-bold">
                      {savingProfile ? 'Saving...' : 'Save Profile'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default EmployeePortal;
